/**
 * @memberof module:util
 * @file rfqOfferCards — maps a presented-RFQ quote set (GET /rfq/:id/quotes from
 * the orchestrator, piece C) into the rate-card JSON the normal results screen
 * already renders. This is what lets the customer offer-view (/rfq/:id/offers)
 * show agent-assembled options as REAL ratesearch cards instead of a bespoke UI
 * (Neil, #78 W5 follow-up): "it should appear as ratesearch cards back in the
 * original ratesearch, not a whole new UI."
 *
 * Strategy (robust by construction): clone the bundled sample card as a template
 * — so every field the heavy card component reads is guaranteed present (no crash
 * from a sparse hand-built card) — then overlay the real offer values (carrier,
 * lane, transit, price) on top. The whole agreed SELL price is folded into the
 * freight line and the view-details sub-sections are zeroed, so the card's own
 * computed total equals the offer's sell exactly; the per-leg breakdown is shown
 * by the Accept panel (MainResultCard) from the `rfqOffer` payload we attach.
 *
 * FCL is implemented here; LCL/Air are added in a follow-up (offerModeOf still
 * classifies them so the page can fall back cleanly until then).
 */

import { getLCLChargeableWeight } from './getLCLChargeableWeight';
import { getAIRChargeableWeight } from './getAIRChargeableWeight';

const clone = (o) => JSON.parse(JSON.stringify(o));

// FCL container-size token (BB / RFQ lane) → ratesearch fclCounters key.
const sizeToCounterKey = (size) => {
  const s = String(size || '').toUpperCase().replace(/['"\s]/g, '');
  if (s.startsWith('20')) return '_20stCounter';
  if (s === '40HC' || s === '40HQ' || s === '40H') return '_40hqCounter';
  if (s.startsWith('45')) return '_45hqCounter';
  return '_40stCounter'; // 40 / 40ST / 40GP and the safe default
};

/**
 * Derive an fclCounters map ({_20stCounter, _40stCounter, _40hqCounter,
 * _45hqCounter}) from an RFQ lane. Handles both lane shapes seen in the wild:
 * `containers: [{size, qty}]` (A's createRfq) and a single `containerSize`
 * string (seed/legacy). Never returns all-zero — a zero-container card shows $0.
 */
export const laneToFclCounters = (lane = {}) => {
  const counters = { _20stCounter: 0, _40stCounter: 0, _40hqCounter: 0, _45hqCounter: 0 };
  if (Array.isArray(lane.containers) && lane.containers.length) {
    lane.containers.forEach((c) => {
      counters[sizeToCounterKey(c.size)] += Number(c.qty) || 1;
    });
  } else if (lane.containerSize) {
    counters[sizeToCounterKey(lane.containerSize)] += 1;
  }
  const total = counters._20stCounter + counters._40stCounter + counters._40hqCounter + counters._45hqCounter;
  if (!total) counters._40stCounter = 1;
  return counters;
};

const containerTotal = (c) =>
  (c._20stCounter || 0) + (c._40stCounter || 0) + (c._40hqCounter || 0) + (c._45hqCounter || 0);

/**
 * Classify which ratesearch mode renders an RFQ: AIR mode → 'air'; OCEAN with
 * containers → 'fcl'; OCEAN without containers (weight/volume) → 'lcl'.
 */
export const offerModeOf = (quotesResp = {}) => {
  if (String(quotesResp.mode || '').toUpperCase() === 'AIR') return 'air';
  const lane = quotesResp.lane || {};
  const hasContainers = (Array.isArray(lane.containers) && lane.containers.length) || lane.containerSize;
  return hasContainers ? 'fcl' : 'lcl';
};

const freightVendorName = (legs = []) => {
  const f = legs.find((l) => l.sectionType === 'FREIGHT');
  return (f && f.vendor) || (legs[0] && legs[0].vendor) || 'Selected carrier';
};

// Derive a weight/volume shipment ({totalWeight, weightUnit, totalVolume,
// volumeUnit}) for LCL/Air from the RFQ lane (passthrough weight/volume fields
// when present), else a sensible default. Drives the card's chargeable-weight
// math and the displayed measure.
const laneToShipment = (lane = {}, mode) => ({
  // A's createRfq posts the LCL/Air shipment on lane.weight / lane.cbm (rfqPayload.js),
  // so read those first; keep totalWeight/weightKg + totalVolume/volumeCbm as
  // fallbacks for other lane shapes. Reading the wrong keys forced every card onto
  // the hardcoded default shipment, making the per-unit rate fictional (#7).
  totalWeight: Number(lane.weight || lane.totalWeight || lane.weightKg) || (mode === 'air' ? 200 : 1000),
  weightUnit: lane.weightUnit || 'KG',
  totalVolume: Number(lane.cbm || lane.totalVolume || lane.volumeCbm) || (mode === 'air' ? 1 : 2),
  volumeUnit: lane.volumeUnit || 'CBM',
});

// Empty the view-details sub-sections on a cloned card so they contribute 0 to
// the card's computed total (the freight line carries the freight; the rest show
// in the offer breakdown). FCL/LCL/Air share these section field names. Fields
// are emptied, not deleted, so the card component still finds them.
const zeroSections = (card) => {
  ['pickup', 'ers', 'delivery'].forEach((k) => {
    card[`${k}_section`] = [];
    card[`${k}_checkable`] = false;
    card[`${k}_checked`] = false;
  });
  card.origin_section = [];
  card.discharge_section = [];
  card.port_fees_from = [];
  card.port_fees_to = [];
  card.destination_extra_charges = [];
  card.origin_checkable = false;
  card.origin_checked = false;
  card.discharge_checkable = false;
  card.discharge_checked = false;
  return card;
};

// Common overlay shared by every mode's offer card: identity, carrier, lane,
// transit, cleaned blurb/ratings, and the authoritative `rfqOffer` payload (the
// per-section breakdown + Accept button read this). Returns the marked-up
// freight portion so each mode sets its own freight field (container price for
// FCL, per-unit rate for LCL/Air).
const applyCommonOffer = (card, quote, lane, rfqStatus) => {
  const sell = Number(quote.sell) || 0;
  const legs = quote.legs || [];
  const freightLeg = legs.find((l) => l.sectionType === 'FREIGHT');
  // C's GET /rfq/:id/quotes already returns every leg's `priceTotal` and each
  // charge `amount` SELL-side (markup applied once at assembly, #c67ea63) — do NOT
  // re-apply markupPct here or the freight line double-markups (seen live: a
  // $2,760 sell rendered as $3,174 = 2760×1.15 while the all-in still read 2,760).
  // The headline freight line is the FREIGHT leg; other sections show in the
  // breakdown. Falls back to the whole sell when there's no freight leg.
  const freightSell = freightLeg ? Number(freightLeg.priceTotal) || 0 : sell;

  card.id = `rfq-offer-${quote.quoteId}`;
  card.uuid = card.id;
  card.currency = quote.currency || card.currency || 'USD';
  // both keys — different card paths read sealine vs company_name
  card.sealine = freightVendorName(legs);
  card.company_name = card.sealine;
  card.is_estimate = false;
  card.show_variable_rate = false;
  card.show_schedule_link = false;
  // drop the sample template's placeholder marketing copy — a customer offer
  // shows the agreed carrier/price, not lorem-ipsum company blurb.
  card.description = '';
  card.company_about = '';
  card.company_description = '';
  card.about = '';
  // an agent-assembled offer carries no marketplace reviews — don't imply any
  card.avg_rating = 0;
  card.no_of_ratings = 0;
  card.no_of_reviews = 0;
  // Show the resolved port NAME ("Shanghai (CNSHA)"), not the bare Company id —
  // C attaches originPortName/destPortName to the lane; fall back to the id only
  // if a name didn't resolve. Ids are for matching/handoffs, never display.
  const originLabel = lane.originPortName || lane.originPort;
  const dischargeLabel = lane.dischargePortName || lane.destPortName || lane.destPort;
  card.port_of_origin = originLabel || card.port_of_origin;
  // RouteTimeBlock reads the discharge port as an OBJECT (`.company_name`),
  // unlike origin which it reads as a plain string — mirror that shape or the
  // discharge node label renders blank.
  card.port_of_discharge = dischargeLabel
    ? { company_name: dischargeLabel, companyName: dischargeLabel, name: dischargeLabel }
    : card.port_of_discharge;
  card.transit_time = quote.totalTransitDays || card.transit_time;
  card.transitTime = quote.totalTransitDays || card.transitTime;
  // keep the card's date gate happy (Book-now branch) — prefer the lane ETD,
  // else ~30 days out so the offer reads as live, not stale.
  card.etd_origin = lane.targetEtd || new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);

  // Authoritative offer payload the Accept panel + breakdown render + commit.
  card.rfqOffer = {
    quoteId: quote.quoteId,
    status: quote.status,
    // The RFQ's own status (not the quote's): the Accept button is gated on this so
    // an ASSEMBLED / EXPIRED / REJECTED RFQ's cards are shown for reference only,
    // never bookable, even when the quote rows are still PRESENTED (#22).
    rfqStatus: rfqStatus,
    sell,
    buy: Number(quote.buy) || 0,
    currency: card.currency,
    markupPct: Number(quote.markupPct) || 0,
    totalTransitDays: quote.totalTransitDays || 0,
    // each section's sell-side amount (C already applied markup once at assembly,
    // #c67ea63) so the breakdown lines sum to the quote sell — passed through, not
    // re-marked.
    legs: legs.map((l) => ({
      sectionType: l.sectionType,
      vendor: l.vendor,
      priceTotal: Number(l.priceTotal) || 0,
      sell: Math.round((Number(l.priceTotal) || 0) * 100) / 100,
      currency: l.currency || card.currency,
      transitTime: l.transitTime || 0,
      // a leg is an estimate when all its charge lines are flagged (instant-estimate
      // sections); the breakdown badges it so the customer knows it isn't firm.
      isEstimate: Array.isArray(l.charges) && l.charges.length > 0 && l.charges.every((c) => c.isEstimate),
      // the section's individual charge lines (unit code = basis, container =
      // size), already sell-side — so the breakdown can show per-charge detail
      // (per-container freight, drayage accessorials, warehouse services).
      charges: (Array.isArray(l.charges) ? l.charges : []).map((c) => ({
        code: c.code,
        basis: c.basis || '',
        size: c.size || '',
        description: c.description || '',
        notes: c.notes || '',
        isEstimate: !!c.isEstimate, // Charges.isEstimate — mark estimate lines in the breakdown
        // unit cost × quantity so the breakdown shows "3 CTN @ $150/CTN" not just a
        // total (#6); `sell` is the LINE TOTAL (C already marked it up once).
        unitCost: Math.round((Number(c.unitCost) || 0) * 100) / 100,
        quantity: Number(c.quantity) || 1,
        sell: Math.round((Number(c.amount) || 0) * 100) / 100,
      })),
    })),
  };
  return { freightSell };
};

// A presented quote → one FCL rate card. Freight rides the container price so
// price × counters === the freight sell.
const buildFclCard = (quote, lane, template, counters, rfqStatus) => {
  const card = zeroSections(clone(template));
  const { freightSell } = applyCommonOffer(card, quote, lane, rfqStatus);
  const perContainer = String(freightSell / (containerTotal(counters) || 1));
  card['20st'] = perContainer;
  card['40st'] = perContainer;
  card['40hq'] = perContainer;
  card['45hq'] = perContainer;
  return card;
};

// A presented quote → one LCL/Air rate card. Their freight is a per-unit rate ×
// chargeable weight, so set the rate to recover the freight sell at the seeded
// shipment's chargeable weight, and surface a human-readable dominant rate.
const buildWeightCard = (quote, lane, template, chargeableWeight, unitLabel, rfqStatus) => {
  const card = zeroSections(clone(template));
  const { freightSell } = applyCommonOffer(card, quote, lane, rfqStatus);
  const cw = chargeableWeight > 0 ? chargeableWeight : 1;
  const perUnit = freightSell / cw;
  card.rate = String(perUnit);
  card.freight_dominant_rate = `${card.currency} ${perUnit.toFixed(2)} ${unitLabel}`;
  return card;
};

// Shared lane → results queryParams (drives the results-screen lane header).
// `id` keeps the numeric port id (matching/handoff); the displayed name/city use
// the resolved port NAME C attaches (fall back to the id only if unresolved).
const laneToQueryParams = (lane, type) => ({
  from: { id: lane.originPort, name: lane.originPortName || lane.originPort, city: lane.originPortName || lane.originPort, country: '', portCode: lane.originPort },
  to: { id: lane.destPort, name: lane.destPortName || lane.destPort, city: lane.destPortName || lane.destPort, country: '', portCode: lane.destPort },
  type,
});

/**
 * Build the rate-card payload for a presented-RFQ quote set.
 *
 * @param {Object}  args
 * @param {Object}  args.quotesResp - GET /rfq/:id/quotes response ({rfqId, status, mode, lane, quotes[]}).
 * @param {Object}  args.templates  - bundled sample fixtures {fcl, lcl, air} (full JSON, each with .rates).
 * @returns {Object} { mode, supported, queryParams, ...mode-specific seed }
 *   FCL → { fclCounters, fclJson }; LCL → { lclShipment, lclJson };
 *   Air → { airShipment, airJson }. `supported` is false only for an unknown mode.
 */
export const buildOfferRateJson = ({ quotesResp, templates }) => {
  const mode = offerModeOf(quotesResp);
  const lane = quotesResp.lane || {};
  const quotes = Array.isArray(quotesResp.quotes) ? quotesResp.quotes : [];
  // The RFQ status gates per-card acceptability (#22) — bookable only when PRESENTED.
  const rfqStatus = quotesResp.status;

  if (mode === 'fcl') {
    const counters = laneToFclCounters(lane);
    const cards = quotes.map((q) => buildFclCard(q, lane, templates.fcl.rates.fcl[0], counters, rfqStatus));
    return {
      mode,
      supported: true,
      queryParams: laneToQueryParams(lane, 'api-fcl'),
      fclCounters: counters,
      fclJson: { ...clone(templates.fcl), rates: { fcl: cards } },
    };
  }

  if (mode === 'lcl') {
    const shipment = laneToShipment(lane, 'lcl');
    const cw = getLCLChargeableWeight(shipment);
    const cards = quotes.map((q) => buildWeightCard(q, lane, templates.lcl.rates.lcl[0], cw, '/ W/M', rfqStatus));
    return {
      mode,
      supported: true,
      queryParams: laneToQueryParams(lane, 'api-lcl'),
      lclShipment: shipment,
      lclJson: { ...clone(templates.lcl), rates: { lcl: cards } },
    };
  }

  if (mode === 'air') {
    const shipment = laneToShipment(lane, 'air');
    const cw = getAIRChargeableWeight(shipment);
    const cards = quotes.map((q) => buildWeightCard(q, lane, templates.air.rates.air[0], cw, '/ KG', rfqStatus));
    return {
      mode,
      supported: true,
      queryParams: laneToQueryParams(lane, 'api-air'),
      airShipment: shipment,
      airJson: { ...clone(templates.air), rates: { air: cards } },
    };
  }

  // Unknown mode — page falls back to its native option list.
  return { mode, supported: false, queryParams: laneToQueryParams(lane, 'api-fcl') };
};
